import {
  StayAmenity,
  StayCategory,
  StayRoomType,
  StaySortOption,
  type ISearchStaysInput,
} from '../../../__generated__/graphql';

export interface StayGuests {
  adults: number;
  children: number;
  infants: number;
  pets: number;
}

export interface StayFilters {
  minPrice?: number;
  maxPrice?: number;
  roomTypes: StayRoomType[];
  minBedrooms?: number;
  minBeds?: number;
  minBathrooms?: number;
  amenities: StayAmenity[];
  instantBook: boolean;
  selfCheckIn: boolean;
  freeCancellation: boolean;
  category?: StayCategory;
}

/** Everything a stays search is, kept in the URL so a link reopens it (spec 016 W12). */
export interface StaySearchState {
  place: string;
  latitude?: number;
  longitude?: number;
  /** The map's box after "search as I move the map". */
  bounds?: { north: number; south: number; east: number; west: number };
  checkIn?: string;
  checkOut?: string;
  guests: StayGuests;
  filters: StayFilters;
  sortBy: StaySortOption;
}

export const DEFAULT_GUESTS: StayGuests = { adults: 1, children: 0, infants: 0, pets: 0 };

export const EMPTY_FILTERS: StayFilters = {
  roomTypes: [],
  amenities: [],
  instantBook: false,
  selfCheckIn: false,
  freeCancellation: false,
};

const DAY = /^\d{4}-\d{2}-\d{2}$/;

const int = (value: string | null, min: number, max: number, fallback: number) => {
  const n = Number(value);
  return value !== null && Number.isInteger(n) && n >= min && n <= max ? n : fallback;
};
const float = (value: string | null) => {
  if (value === null || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};
const list = <T extends string>(value: string | null, allowed: readonly string[]): T[] =>
  (value ?? '').split(',').filter(item => allowed.includes(item)) as T[];

export function parseStaySearch(params: URLSearchParams): StaySearchState {
  const checkIn = params.get('checkIn') ?? undefined;
  const checkOut = params.get('checkOut') ?? undefined;
  const validDates =
    !!checkIn && !!checkOut && DAY.test(checkIn) && DAY.test(checkOut) && checkIn < checkOut;
  const boundsParts = (params.get('bounds') ?? '').split(',').map(Number);
  const bounds =
    boundsParts.length === 4 && boundsParts.every(Number.isFinite)
      ? { north: boundsParts[0], south: boundsParts[1], east: boundsParts[2], west: boundsParts[3] }
      : undefined;
  const sort = params.get('sort');
  const category = params.get('category');
  return {
    place: params.get('place') ?? '',
    latitude: float(params.get('lat')),
    longitude: float(params.get('lng')),
    bounds,
    checkIn: validDates ? checkIn : undefined,
    checkOut: validDates ? checkOut : undefined,
    guests: {
      adults: int(params.get('adults'), 1, 16, 1),
      children: int(params.get('children'), 0, 15, 0),
      infants: int(params.get('infants'), 0, 5, 0),
      pets: int(params.get('pets'), 0, 5, 0),
    },
    filters: {
      minPrice: float(params.get('minPrice')),
      maxPrice: float(params.get('maxPrice')),
      roomTypes: list<StayRoomType>(params.get('roomTypes'), Object.values(StayRoomType)),
      minBedrooms: float(params.get('minBedrooms')),
      minBeds: float(params.get('minBeds')),
      minBathrooms: float(params.get('minBathrooms')),
      amenities: list<StayAmenity>(params.get('amenities'), Object.values(StayAmenity)),
      instantBook: params.get('instantBook') === '1',
      selfCheckIn: params.get('selfCheckIn') === '1',
      freeCancellation: params.get('freeCancellation') === '1',
      category:
        category && (Object.values(StayCategory) as string[]).includes(category)
          ? (category as StayCategory)
          : undefined,
    },
    sortBy:
      sort && (Object.values(StaySortOption) as string[]).includes(sort)
        ? (sort as StaySortOption)
        : StaySortOption.Recommended,
  };
}

/** The query string for a search; defaults are left out so links stay short. */
export function staySearchQuery(state: StaySearchState): string {
  const params = new URLSearchParams();
  const set = (key: string, value: unknown, skip?: unknown) => {
    if (value === undefined || value === null || value === '' || value === skip) return;
    params.set(key, String(value));
  };
  set('place', state.place);
  set('lat', state.latitude);
  set('lng', state.longitude);
  if (state.bounds) {
    const { north, south, east, west } = state.bounds;
    params.set('bounds', [north, south, east, west].map(n => n.toFixed(5)).join(','));
  }
  set('checkIn', state.checkIn);
  set('checkOut', state.checkOut);
  set('adults', state.guests.adults, 1);
  set('children', state.guests.children, 0);
  set('infants', state.guests.infants, 0);
  set('pets', state.guests.pets, 0);
  const f = state.filters;
  set('minPrice', f.minPrice);
  set('maxPrice', f.maxPrice);
  if (f.roomTypes.length) params.set('roomTypes', f.roomTypes.join(','));
  set('minBedrooms', f.minBedrooms);
  set('minBeds', f.minBeds);
  set('minBathrooms', f.minBathrooms);
  if (f.amenities.length) params.set('amenities', f.amenities.join(','));
  if (f.instantBook) params.set('instantBook', '1');
  if (f.selfCheckIn) params.set('selfCheckIn', '1');
  if (f.freeCancellation) params.set('freeCancellation', '1');
  set('category', f.category);
  set('sort', state.sortBy, StaySortOption.Recommended);
  return params.toString();
}

/** The API input. A box wins over a point; without either the whole site is searched. */
export function toSearchStaysInput(
  state: StaySearchState,
  page = 1,
  limit = 24,
): ISearchStaysInput {
  const f = state.filters;
  return {
    bounds: state.bounds ?? null,
    where:
      !state.bounds && state.latitude !== undefined && state.longitude !== undefined
        ? // The API's radius is in miles on every brand (spec 028 D3); results show no distance.
          { latitude: state.latitude, longitude: state.longitude, radiusMiles: 25 }
        : null,
    checkIn: state.checkIn ?? null,
    checkOut: state.checkOut ?? null,
    guests: state.guests,
    filters: {
      minPrice: f.minPrice ?? null,
      maxPrice: f.maxPrice ?? null,
      roomTypes: f.roomTypes.length ? f.roomTypes : null,
      minBedrooms: f.minBedrooms ?? null,
      minBeds: f.minBeds ?? null,
      minBathrooms: f.minBathrooms ?? null,
      amenities: f.amenities.length ? f.amenities : null,
      instantBook: f.instantBook || null,
      selfCheckIn: f.selfCheckIn || null,
      freeCancellation: f.freeCancellation || null,
      category: f.category ?? null,
    },
    sortBy: state.sortBy,
    page,
    limit,
  };
}

/** "3 guests, 1 infant, 1 pet" (Airbnb counts infants and pets apart). */
export function guestSummary(guests: StayGuests): string {
  const people = guests.adults + guests.children;
  const parts = [`${people} guest${people === 1 ? '' : 's'}`];
  if (guests.infants) parts.push(`${guests.infants} infant${guests.infants === 1 ? '' : 's'}`);
  if (guests.pets) parts.push(`${guests.pets} pet${guests.pets === 1 ? '' : 's'}`);
  return parts.join(', ');
}

/** How many filters are on, for the Filters button badge. */
export function activeFilterCount(filters: StayFilters): number {
  return [
    filters.minPrice !== undefined || filters.maxPrice !== undefined,
    filters.roomTypes.length > 0,
    !!filters.minBedrooms || !!filters.minBeds || !!filters.minBathrooms,
    filters.amenities.length > 0,
    filters.instantBook,
    filters.selfCheckIn,
    filters.freeCancellation,
  ].filter(Boolean).length;
}

/** "Oct 20 – 23" style range, or "Add dates". */
export function dateRangeLabel(checkIn?: string, checkOut?: string): string {
  if (!checkIn || !checkOut) return 'Add dates';
  const a = new Date(`${checkIn}T00:00:00`);
  const b = new Date(`${checkOut}T00:00:00`);
  const month = (d: Date) => d.toLocaleString('en-US', { month: 'short' });
  return a.getMonth() === b.getMonth()
    ? `${month(a)} ${a.getDate()} to ${b.getDate()}`
    : `${month(a)} ${a.getDate()} to ${month(b)} ${b.getDate()}`;
}
